// NOTE: Deliberately importing from @apollo/client
import { useQuery } from '@apollo/client'
import { Settings, Lock, FolderSearch } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/sv/sidebar'
import { useCompanyPath, useIdCompany, useRBAC } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { OBJECT_STORAGE_DISPLAY_NAME_QUERY } from '@/routes/object-storage/queries'
import type {
  ObjectStorageDisplayNameQuery,
  ObjectStorageDisplayNameQueryVariables,
} from '@/routes/object-storage/queries.types.gen'

const items = [
  {
    title: 'Objects',
    url: '/object-storage/:idObjectStorage/objects',
    icon: FolderSearch,
    activeTags: ['objects'],
  },
  {
    title: 'Settings',
    url: '/object-storage/:idObjectStorage/settings',
    icon: Settings,
    activeTags: ['/settings'],
  },
] as const

export const ObjectStorageNav = () => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const location = useLocation()
  const { getCompanyPath } = useCompanyPath()
  const { idObjectStorage = '' } = useParams<{ idObjectStorage: string }>()
  const { data } = useQuery<
    ObjectStorageDisplayNameQuery,
    ObjectStorageDisplayNameQueryVariables
  >(OBJECT_STORAGE_DISPLAY_NAME_QUERY, {
    variables: { idCompany, idObjectStorage },
    fetchPolicy: 'cache-only',
  })

  const idProjectObjectStorage = useMemo(
    () => data?.company?.objectStorage?.idProject ?? '',
    [data]
  )
  const hasAccess = useMemo(
    () =>
      data === undefined
        ? true
        : rbac
            .company(idCompany)
            .project(idProjectObjectStorage)
            .objectStorage(idObjectStorage)
            .view.can(),
    [rbac, idCompany, idObjectStorage, idProjectObjectStorage, data]
  )

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const isActive = item.activeTags.some((tag) =>
              location.pathname.includes(tag)
            )
            return (
              <SidebarMenuItem
                key={item.url}
                title={item.title}
                className={cn(
                  !hasAccess
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : undefined
                )}
              >
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  disabled={!hasAccess}
                >
                  {hasAccess ? (
                    <Link
                      to={getCompanyPath({
                        pathname: item.url,
                        variables: { idObjectStorage },
                      })}
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Lock />
                      <span>{item.title}</span>
                    </div>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
